import { useState } from "react";
import { useParams, useNavigate, generatePath } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  Stack,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  getAdminUserDetail,
  updateUserStatus,
  updateUserRole,
  resetUserChangeLimit,
  clearDeletionCooldown,
  flagUserForVerification,
  setUserRecommendationVisibility,
} from "../../../utility/httpActions/adminActions";
import { frontendRoutes } from "../../../utility/frontendRoutes";
import { resolveImageUri } from "../../../utility/constants";
import useAuthentication from "../../../hooks/useAuthentication";
import Avatar from "../../../common-components/Avatar";

const statusColor = {
  ACTIVE: "success",
  SUSPENDED: "warning",
  DEACTIVATED: "error",
};
const verificationStatusColor = {
  NOT_SUBMITTED: "default",
  PENDING: "info",
  APPROVED: "success",
  REJECTED: "error",
};

const StatusDialog = ({
  open,
  onClose,
  targetStatus,
  onConfirm,
  loading,
  requireReason,
}) => {
  const [reason, setReason] = useState("");
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {targetStatus === "SUSPENDED" && "Suspend Account"}
        {targetStatus === "DEACTIVATED" && "Deactivate Account"}
        {targetStatus === "ACTIVE" && "Reactivate Account"}
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          multiline
          minRows={2}
          label={requireReason ? "Reason (required)" : "Reason (optional)"}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color={targetStatus === "ACTIVE" ? "success" : "error"}
          disabled={loading || (requireReason && !reason.trim())}
          onClick={() => {
            onConfirm(reason);
            setReason("");
          }}
        >
          {loading ? <CircularProgress size={18} /> : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const RoleDialog = ({ open, onClose, grantingAdmin, onConfirm, loading }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle>
      {grantingAdmin ? "Grant Admin Access" : "Remove Admin Access"}
    </DialogTitle>
    <DialogContent>
      <Typography variant="body2">
        {grantingAdmin
          ? "This user will be able to access all devAdmin tools, including suspending accounts, resolving disputes, and managing other users. Only grant this to people you trust."
          : "This user will immediately lose access to all devAdmin tools."}
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button
        variant="contained"
        color={grantingAdmin ? "primary" : "error"}
        disabled={loading}
        onClick={onConfirm}
      >
        {loading ? <CircularProgress size={18} /> : "Confirm"}
      </Button>
    </DialogActions>
  </Dialog>
);

const UserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { currentUser } = useAuthentication();
  const [dialog, setDialog] = useState(null);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-user", userId],
    queryFn: () => getAdminUserDetail(userId),
  });

  const mutation = useMutation({
    mutationFn: ({ status, reason }) =>
      updateUserStatus(userId, { status, reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-user", userId] });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      qc.invalidateQueries({ queryKey: ["admin-dashboard"] });
      setDialog(null);
      setSnackbar({ severity: "success", message: "Account status updated." });
    },
    onError: (err) => {
      setSnackbar({
        severity: "error",
        message: err?.data?.message || "Failed to update.",
      });
    },
  });

  const resetLimitMutation = useMutation({
    mutationFn: ({ field }) => resetUserChangeLimit(userId, { field }),
    onSuccess: (_, { field }) => {
      qc.invalidateQueries({ queryKey: ["admin-user", userId] });
      setSnackbar({
        severity: "success",
        message: `${field} change limit reset.`,
      });
    },
    onError: (err) => {
      setSnackbar({
        severity: "error",
        message: err?.data?.message || "Failed to reset limit.",
      });
    },
  });

  const cooldownMutation = useMutation({
    mutationFn: () => clearDeletionCooldown(userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-user", userId] });
      setSnackbar({
        severity: "success",
        message: "Deletion cooldown cleared.",
      });
    },
    onError: (err) => {
      setSnackbar({
        severity: "error",
        message: err?.data?.message || "Failed to clear cooldown.",
      });
    },
  });

  const flagVerificationMutation = useMutation({
    mutationFn: ({ required }) => flagUserForVerification(userId, { required }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-user", userId] });
      setSnackbar({
        severity: "success",
        message: "Verification flag updated.",
      });
    },
    onError: (err) => {
      setSnackbar({
        severity: "error",
        message: err?.data?.message || "Failed.",
      });
    },
  });

  const recommendationsMutation = useMutation({
    mutationFn: ({ hidden }) =>
      setUserRecommendationVisibility(userId, { hidden }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-user", userId] });
      setSnackbar({
        severity: "success",
        message: "Recommendation visibility updated.",
      });
    },
    onError: (err) => {
      setSnackbar({
        severity: "error",
        message: err?.data?.message || "Failed.",
      });
    },
  });

  const roleMutation = useMutation({
    mutationFn: ({ isAdmin }) => updateUserRole(userId, { isAdmin }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-user", userId] });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      setRoleDialogOpen(false);
      setSnackbar({ severity: "success", message: "Admin role updated." });
    },
    onError: (err) => {
      setSnackbar({
        severity: "error",
        message: err?.data?.message || "Failed to update admin role.",
      });
    },
  });

  if (isLoading) return <CircularProgress />;
  if (error || !data) return <Alert severity="error">User not found.</Alert>;

  const { user, orderStats, reports, deletionRecord, shops } = data;
  const currentStatus = user.accountStatus || "ACTIVE";
  const isAdmin = user.roles?.includes("admin");
  const isSelf = currentUser && (currentUser.id || currentUser._id) === userId;

  const cooldownExpiresAt =
    deletionRecord && !deletionRecord.cooldownOverriddenAt
      ? new Date(deletionRecord.cooldownEndsAt)
      : null;
  const isCooldownActive = cooldownExpiresAt && cooldownExpiresAt > new Date();

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(frontendRoutes.ADMIN_USERS)}
        sx={{ mb: 2 }}
      >
        Back to Users
      </Button>
      <Typography variant="h5" fontWeight={700} mb={3}>
        {user.name}
      </Typography>

      <Stack
        spacing={2}
        direction={{ xs: "column", md: "row" }}
        alignItems="flex-start"
      >
        {/* Profile card */}
        <Card variant="outlined" sx={{ flex: 1, minWidth: 0 }}>
          <CardContent>
            <Typography fontWeight={600} mb={1}>
              Profile
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">
                Email: {user.email || "—"}
              </Typography>
              {user.email && (
                <Chip
                  label={user.isEmailVerified ? "Verified" : "Unverified"}
                  color={user.isEmailVerified ? "success" : "default"}
                  size="small"
                />
              )}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">
                Phone: {user.phoneNumber || "—"}
              </Typography>
              {user.phoneNumber && (
                <Chip
                  label={user.isPhoneVerified ? "Verified" : "Unverified"}
                  color={user.isPhoneVerified ? "success" : "default"}
                  size="small"
                />
              )}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
              <Typography variant="body2">Sign-in methods:</Typography>
              <Chip
                label="Google"
                size="small"
                color={
                  user.authProviders?.includes("google") ? "success" : "default"
                }
                variant={
                  user.authProviders?.includes("google") ? "filled" : "outlined"
                }
              />
              <Chip
                label="Apple"
                size="small"
                color={
                  user.authProviders?.includes("apple") ? "success" : "default"
                }
                variant={
                  user.authProviders?.includes("apple") ? "filled" : "outlined"
                }
              />
              <Chip
                label="Password"
                size="small"
                color={user.hasPassword ? "success" : "default"}
                variant={user.hasPassword ? "filled" : "outlined"}
              />
            </Stack>
            <Typography variant="body2">
              Country: {user.country || "—"}
            </Typography>
            <Typography variant="body2">
              Joined: {new Date(user.createdAt).toLocaleDateString()}
            </Typography>
            <Typography variant="body2">
              Last login:{" "}
              {user.lastLoginAt
                ? `${new Date(user.lastLoginAt).toLocaleString()}${
                    user.lastLoginPlatform ? ` (${user.lastLoginPlatform})` : ""
                  }`
                : "—"}
            </Typography>
            <Typography variant="body2">
              Last active:{" "}
              {user.lastActiveAt
                ? `${new Date(user.lastActiveAt).toLocaleString()}${
                    user.lastActivePlatform
                      ? ` (${user.lastActivePlatform})`
                      : ""
                  }`
                : "—"}
            </Typography>
            <Stack direction="row" spacing={1} mt={1}>
              <Chip
                label={currentStatus}
                color={statusColor[currentStatus]}
                size="small"
              />
              {user.roles?.includes("admin") && (
                <Chip label="Admin" color="primary" size="small" />
              )}
            </Stack>
            {user.accountStatusReason && (
              <Typography
                variant="caption"
                color="text.secondary"
                mt={0.5}
                display="block"
              >
                Reason: {user.accountStatusReason}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Actions card */}
        <Card variant="outlined" sx={{ minWidth: 200 }}>
          <CardContent>
            <Typography fontWeight={600} mb={1}>
              Actions
            </Typography>
            <Stack spacing={1}>
              {currentStatus === "ACTIVE" && (
                <>
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => setDialog("SUSPENDED")}
                  >
                    Suspend
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setDialog("DEACTIVATED")}
                  >
                    Deactivate
                  </Button>
                </>
              )}
              {currentStatus === "SUSPENDED" && (
                <>
                  <Button
                    variant="outlined"
                    color="success"
                    onClick={() => setDialog("ACTIVE")}
                  >
                    Reactivate
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setDialog("DEACTIVATED")}
                  >
                    Deactivate
                  </Button>
                </>
              )}
              {currentStatus === "DEACTIVATED" && (
                <Button
                  variant="outlined"
                  color="success"
                  onClick={() => setDialog("ACTIVE")}
                >
                  Restore Account
                </Button>
              )}
              <Button
                variant="outlined"
                color="warning"
                disabled={flagVerificationMutation.isPending}
                onClick={() =>
                  flagVerificationMutation.mutate({
                    required: !user.identityVerificationRequired,
                  })
                }
              >
                {user.identityVerificationRequired
                  ? "Clear Verification Flag"
                  : "Flag for Verification"}
              </Button>
              <Button
                variant="outlined"
                disabled={recommendationsMutation.isPending}
                title="Hides this person and their shops from the feed's follow suggestions."
                onClick={() =>
                  recommendationsMutation.mutate({
                    hidden: !user.hiddenFromRecommendations,
                  })
                }
              >
                {user.hiddenFromRecommendations
                  ? "Show in Recommendations"
                  : "Hide from Recommendations"}
              </Button>
              <Button
                variant="outlined"
                color={isAdmin ? "error" : "primary"}
                disabled={isSelf && isAdmin}
                title={
                  isSelf && isAdmin
                    ? "You cannot remove your own admin access."
                    : undefined
                }
                onClick={() => setRoleDialogOpen(true)}
              >
                {isAdmin ? "Remove Admin Access" : "Make Admin"}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      {/* Verification */}
      <Card variant="outlined" sx={{ mt: 2 }}>
        <CardContent>
          <Typography fontWeight={600} mb={1}>
            Verification
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <Chip
              label={user.verificationStatus || "NOT_SUBMITTED"}
              color={
                verificationStatusColor[
                  user.verificationStatus || "NOT_SUBMITTED"
                ]
              }
              size="small"
            />
            {user.identityVerificationRequired && (
              <Chip
                label="Flagged for verification"
                color="warning"
                size="small"
              />
            )}
          </Stack>
          {user.verifiedAt && (
            <Typography variant="body2">
              Verified: {new Date(user.verifiedAt).toLocaleDateString()}
            </Typography>
          )}
          {user.verificationRejectionReason && (
            <Typography variant="body2" color="text.secondary">
              Last rejection reason: {user.verificationRejectionReason}
            </Typography>
          )}
          {user.verificationStatus === "PENDING" && (
            <Button
              size="small"
              sx={{ mt: 1 }}
              onClick={() =>
                navigate(
                  generatePath(frontendRoutes.ADMIN_VERIFICATION_DETAIL, {
                    userId,
                  }),
                )
              }
            >
              Review Submission →
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Shop(s) owned */}
      <Card variant="outlined" sx={{ mt: 2 }}>
        <CardContent>
          <Typography fontWeight={600} mb={1}>
            {shops?.length > 1 ? "Shops" : "Shop"}
          </Typography>
          {shops?.length > 0 ? (
            <Stack spacing={1.5}>
              {shops.map((shop) => {
                const shopId = shop.id || shop._id;
                return (
                  <Stack
                    key={shopId}
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                  >
                    <Avatar
                      src={
                        shop.profilePhoto?.path
                          ? resolveImageUri(shop.profilePhoto)
                          : null
                      }
                      name={shop.shopName}
                      size={36}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {shop.shopName}
                    </Typography>
                    {shop.isIdentityVerified && (
                      <Chip label="Verified" color="success" size="small" />
                    )}
                    <Chip label={shop.status} size="small" />
                    <IconButton
                      component="a"
                      href={generatePath(frontendRoutes.ADMIN_SHOP_DETAIL, {
                        shopId,
                      })}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      title="Open shop admin page in a new tab"
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                );
              })}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              This user does not own a shop.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Order stats */}
      <Card variant="outlined" sx={{ mt: 2 }}>
        <CardContent>
          <Typography fontWeight={600} mb={1}>
            Order History (as buyer)
          </Typography>
          {orderStats?.length ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orderStats.map(({ _id, count }) => (
                  <TableRow key={_id}>
                    <TableCell>
                      <Chip label={_id} size="small" />
                    </TableCell>
                    <TableCell>{count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No orders found.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Reports against user */}
      <Card variant="outlined" sx={{ mt: 2 }}>
        <CardContent>
          <Typography fontWeight={600} mb={1}>
            Reports Against This User ({reports?.length || 0})
          </Typography>
          {reports?.length ? (
            reports.map((r) => (
              <Box key={r._id} py={0.5}>
                <Typography variant="body2">
                  {r.reason?.replace(/_/g, " ")} —{" "}
                  <Chip label={r.status} size="small" /> —{" "}
                  {new Date(r.createdAt).toLocaleDateString()}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No reports.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Change Limits */}
      <Card variant="outlined" sx={{ mt: 2 }}>
        <CardContent>
          <Typography fontWeight={600} mb={0.5}>
            Change Limits
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Reset a field lock so the user can update it immediately.
          </Typography>
          {[
            {
              label: "Name",
              field: "name",
              lastChangedAt: user.nameLastChangedAt,
              days: 90,
            },
            {
              label: "Email",
              field: "email",
              lastChangedAt: user.emailLastChangedAt,
              days: 90,
            },
            {
              label: "Phone",
              field: "phone",
              lastChangedAt: user.phoneLastChangedAt,
              days: 90,
            },
          ].map(({ label, field, lastChangedAt, days }) => {
            const nextDate = lastChangedAt
              ? new Date(
                  new Date(lastChangedAt).getTime() +
                    days * 24 * 60 * 60 * 1000,
                )
              : null;
            const isLocked = nextDate && nextDate > new Date();
            return (
              <Stack
                key={field}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                py={0.75}
              >
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {label}
                  </Typography>
                  {lastChangedAt ? (
                    <Typography
                      variant="caption"
                      color={isLocked ? "warning.main" : "text.secondary"}
                    >
                      {isLocked
                        ? `Locked until ${nextDate.toLocaleDateString()}`
                        : `Last changed ${new Date(lastChangedAt).toLocaleDateString()}`}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Never changed
                    </Typography>
                  )}
                </Box>
                {isLocked && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    disabled={resetLimitMutation.isPending}
                    onClick={() => resetLimitMutation.mutate({ field })}
                  >
                    Reset
                  </Button>
                )}
              </Stack>
            );
          })}
        </CardContent>
      </Card>

      {/* Deletion Cooldown */}
      {deletionRecord && (
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent>
            <Typography fontWeight={600} mb={0.5}>
              Account Deletion
            </Typography>
            {deletionRecord.userEmail || deletionRecord.userPhoneNumber ? (
              <>
                <Typography variant="body2">
                  Original phone: {deletionRecord.userPhoneNumber || "—"}
                </Typography>
                <Typography variant="body2">
                  Original email: {deletionRecord.userEmail || "—"}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Original phone and email erased when the 90-day re-registration
                hold ended.
              </Typography>
            )}
            <Typography variant="body2">
              Deleted on:{" "}
              {new Date(deletionRecord.createdAt).toLocaleDateString()}
            </Typography>
            {deletionRecord.reason && (
              <Typography variant="body2">
                Reason: {deletionRecord.reason.replace(/_/g, " ")}
              </Typography>
            )}
            <Divider sx={{ my: 1.5 }} />
            {deletionRecord.cooldownOverriddenAt ? (
              <Typography variant="body2" color="success.main">
                Cooldown cleared by admin on{" "}
                {new Date(
                  deletionRecord.cooldownOverriddenAt,
                ).toLocaleDateString()}
              </Typography>
            ) : isCooldownActive ? (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="body2" color="warning.main">
                  Cooldown expires on {cooldownExpiresAt.toLocaleDateString()}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  disabled={cooldownMutation.isPending}
                  onClick={() => cooldownMutation.mutate()}
                >
                  {cooldownMutation.isPending ? (
                    <CircularProgress size={16} />
                  ) : (
                    "Clear Cooldown"
                  )}
                </Button>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Cooldown expired
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      <StatusDialog
        open={!!dialog}
        onClose={() => setDialog(null)}
        targetStatus={dialog}
        requireReason={dialog === "DEACTIVATED" || dialog === "ACTIVE"}
        loading={mutation.isPending}
        onConfirm={(reason) => mutation.mutate({ status: dialog, reason })}
      />

      <RoleDialog
        open={roleDialogOpen}
        onClose={() => setRoleDialogOpen(false)}
        grantingAdmin={!isAdmin}
        loading={roleMutation.isPending}
        onConfirm={() => roleMutation.mutate({ isAdmin: !isAdmin })}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
      >
        <Alert severity={snackbar?.severity} onClose={() => setSnackbar(null)}>
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserDetail;
